import { useCallback, useEffect, useState } from 'react';
import { AiMode, AiSwitch, getAiSwitch } from '../../../api/cafAutomation';
import { getIntakeControl } from '../../../api/cafWorkflowRuns';
import { isMissingRoute } from '../saveEngine';

export type LoadStatus = 'loading' | 'ready' | 'missing' | 'error';

/** Stored legacy values read as the new switch (spec §6.2): disabled and observe are On, enabled is Watching. */
export const legacyMode = (raw: unknown): AiMode => {
  if (raw === 'on' || raw === 'watching' || raw === 'off') return raw;
  if (raw === 'disabled' || raw === 'observe') return 'on';
  if (raw === 'enabled') return 'watching';
  return 'off';
};

export interface AiSwitchState { status: LoadStatus; value: AiSwitch | null; reload: () => void }

/**
 * The AI switch. `missing` means an older backend (the route answers the app's 404):
 * the pill then shows the legacy intake value, mapped, and the old controls stay (spec §6.12).
 */
export const useAiSwitch = (enabled = true): AiSwitchState => {
  const [status, setStatus] = useState<LoadStatus>(enabled ? 'loading' : 'error');
  const [value, setValue] = useState<AiSwitch | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((count) => count + 1), []);

  useEffect(() => {
    if (!enabled) {
      setStatus('error');
      return undefined;
    }
    setStatus('loading');
    const controller = new AbortController();
    const { signal } = controller;
    const run = async () => {
      try {
        const current = await getAiSwitch(signal);
        if (signal.aborted) return;
        if (!current || !current.mode) throw new Error('unexpected switch');
        setValue(current);
        setStatus('ready');
      } catch (err) {
        if (signal.aborted) return;
        if (!isMissingRoute(err)) {
          setStatus('error');
          return;
        }
        let mapped: AiSwitch | null = null;
        try {
          const intake = await getIntakeControl(signal);
          const mode = legacyMode(intake?.mode);
          if (mode) mapped = { mode, epoch: intake.epoch, raw: intake.mode, legacy: true };
        } catch {
          mapped = null;
        }
        if (signal.aborted) return;
        setValue(mapped);
        setStatus('missing');
      }
    };
    void run();
    return () => controller.abort();
  }, [enabled, tick]);

  return { status, value, reload };
};
